// @ts-nocheck
import _ from 'lodash';
import PropTypes from 'prop-types';
import omitEmpty from 'omit-empty';
import {
  makeStyles,
  Box,
  Grid,
  Icon,
  MenuItem,
  TextField,
  Button,
  Typography,
  FormControlLabel,
  Switch,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { useIfMounted } from '../../../../common/hooks';

const useStyles = makeStyles(theme => ({
  container: {
    flex: '1',
    display: 'flex',
  },
  form: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    '& .MuiFormControl-root': {
      minHeight: '78px',
    },
    '& .MuiSelect-root': {
      maxWidth: '450px',
    },
  },
  formContent: {
    flex: '1',
    display: 'flex',
    flexWrap: 'wrap',
    padding: theme.spacing(1, 3),
    overflow: 'auto',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(1),
  },
  button: {
    margin: theme.spacing(1),
    '&:last-child': {
      marginRight: '0',
    },
  },
}));

function ServiceMessageForm(props) {
  const [t] = useTranslation();
  const { control, errors, formState, watch, reset, register, handleSubmit } = useForm();
  const classes = useStyles();
  const ifMounted = useIfMounted();

  const {
    serviceMessage,
    platforms,
    onSave,
    onCancel,
    actionsDisabled,
    editMode,
    cancelButtonText,
    cancelButtonIcon,
    saveButtonText,
    saveButtonIcon,
  } = props;

  const severity = watch('severity', _.defaultTo(serviceMessage?.severity, 3));
  const { isDirty, isSubmitting } = formState;

  const onSubmit = async data => {
    const formData = omitEmpty(data);
    if (serviceMessage?.id) {
      formData.id = serviceMessage.id;
    }
    try {
      await onSave(formData);
      ifMounted(() => reset(data));
    } catch (e) {
      // Error already handled
    }
  };

  const validateAffectedPlatforms = value => {
    if (_.isEmpty(value)) {
      return t('serviceMessage.serviceMessageAffectedRequired');
    }
    return true;
  };

  return (
    <Box id="ServiceMessageForm-container" className={classes.container}>
      <form id="ServiceMessageForm-form" className={classes.form} onSubmit={handleSubmit(onSubmit)}>
        <Box className={classes.formContent}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Controller
                name="severity"
                control={control}
                defaultValue={_.defaultTo(serviceMessage?.severity, 3)}
                as={
                  <TextField
                    id="ServiceMessageForm-severity"
                    label={t('serviceMessage.serviceMessageSeverity')}
                    variant="outlined"
                    fullWidth
                    select
                  >
                    <MenuItem value={1}>{t('serviceMessage.serviceMessageWithRecovery')}</MenuItem>
                    <MenuItem value={3}>{t('serviceMessage.serviceMessage')}</MenuItem>
                  </TextField>
                }
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="affected"
                control={control}
                defaultValue={_.defaultTo(serviceMessage?.affected, [])}
                rules={{
                  validate: validateAffectedPlatforms,
                }}
                as={
                  <TextField
                    id="ServiceMessageForm-affected"
                    label={t('serviceMessage.serviceMessageAffected')}
                    variant="outlined"
                    SelectProps={{
                      multiple: true,
                    }}
                    error={!!errors.affected}
                    helperText={errors.affected?.message}
                    select
                    fullWidth
                  >
                    {platforms.map(platform => (
                      <MenuItem key={platform} value={platform}>
                        {platform}
                      </MenuItem>
                    ))}
                  </TextField>
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                id="ServiceMessageForm-link"
                name="link"
                label={t('serviceMessage.serviceMessageLink')}
                variant="outlined"
                defaultValue={serviceMessage?.link}
                InputLabelProps={{ shrink: editMode }}
                inputRef={register}
                error={!!errors.link}
                helperText={errors.link?.message}
                disabled={severity === 3}
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <Controller
                name="state"
                control={control}
                defaultValue={_.defaultTo(serviceMessage?.state, 'open')}
                as={
                  <TextField
                    id="ServiceMessageForm-state"
                    label={t('serviceMessage.serviceMessageState')}
                    variant="outlined"
                    fullWidth
                    select
                  >
                    <MenuItem value="open">{t('serviceMessage.serviceMessageStateOpen')}</MenuItem>
                    <MenuItem value="closed">
                      {t('serviceMessage.serviceMessageStateClosed')}
                    </MenuItem>
                  </TextField>
                }
              />
            </Grid>
            <Grid item xs={2}>
              <FormControlLabel
                label="Recovery"
                labelPlacement="top"
                className={classes.switch}
                control={
                  <Controller
                    name="recoveryEnabled"
                    control={control}
                    defaultValue={_.defaultTo(serviceMessage?.recoveryEnabled, false)}
                    render={params => (
                      <Switch
                        id="ServiceMessageForm-recoveryEnabled"
                        checked={params.value}
                        onChange={e => params.onChange(e.target.checked)}
                      />
                    )}
                  />
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="ServiceMessageForm-description"
                name="description"
                label={t('serviceMessage.serviceMessageDescription')}
                variant="outlined"
                defaultValue={serviceMessage?.description}
                InputLabelProps={{ shrink: editMode }}
                inputRef={register({
                  required: t('serviceMessage.serviceMessageDescriptionRequired'),
                })}
                error={!!errors.description}
                helperText={errors.description?.message}
                rows={4}
                multiline
                fullWidth
              />
            </Grid>
          </Grid>
        </Box>
        <Box className={classes.actions}>
          <Button
            id="ServiceMessageForm-cancel"
            className={classes.button}
            startIcon={<Icon>{_.defaultTo(cancelButtonIcon, 'cancel')}</Icon>}
            onClick={onCancel}
            disabled={actionsDisabled || isSubmitting}
          >
            <Typography variant="button" noWrap>
              {_.defaultTo(cancelButtonText, t('common.cancel'))}
            </Typography>
          </Button>
          <Button
            id="ServiceMessageForm-save"
            type="submit"
            color="primary"
            className={classes.button}
            startIcon={<Icon>{_.defaultTo(saveButtonIcon, 'save')}</Icon>}
            disabled={actionsDisabled || isSubmitting || !isDirty}
          >
            <Typography variant="button" noWrap>
              {_.defaultTo(saveButtonText, t('common.save'))}
            </Typography>
          </Button>
        </Box>
      </form>
    </Box>
  );
}

ServiceMessageForm.defaultProps = {
  serviceMessage: undefined,
  cancelButtonText: undefined,
  cancelButtonIcon: undefined,
  saveButtonText: undefined,
  saveButtonIcon: undefined,
  actionsDisabled: undefined,
  editMode: undefined,
};

ServiceMessageForm.propTypes = {
  serviceMessage: PropTypes.shape({
    id: PropTypes.string,
    description: PropTypes.string,
    severity: PropTypes.number,
    state: PropTypes.string,
    affected: PropTypes.arrayOf(PropTypes.string),
    link: PropTypes.string,
    created: PropTypes.string,
    updated: PropTypes.string,
  }),
  platforms: PropTypes.arrayOf(PropTypes.string).isRequired,
  cancelButtonText: PropTypes.string,
  cancelButtonIcon: PropTypes.string,
  saveButtonText: PropTypes.string,
  saveButtonIcon: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  actionsDisabled: PropTypes.bool,
  editMode: PropTypes.bool,
};

export default ServiceMessageForm;
